import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronLeft, Info, Loader2, Search, Users } from "lucide-react";
import { API, JURISDICTIONS } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const PartyHistoryPage = () => {
  const [form, setForm] = useState({ party_name: "", party_type: "individual", jurisdiction: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const analyze = async () => {
    if (!form.party_name.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/parties/analyze`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="party-history-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Party Litigation History</h1>
            <Badge className="bg-pink-500">LIVE DATA</Badge>
          </div>
          <p className="text-muted-foreground">Historical litigation records from public court filings</p>
        </div>
        
        <Alert className="mb-6 bg-slate-100 border-slate-200">
          <Info className="h-4 w-4" />
          <AlertDescription>
            This tool searches historical public court records for litigation involving a party. 
            It provides factual filing information only, without character assessment or inference.
          </AlertDescription>
        </Alert>
        
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-1">
            <CardHeader><CardTitle>Search Party</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Party Name</Label>
                <Input 
                  value={form.party_name} 
                  onChange={e => setForm(p => ({ ...p, party_name: e.target.value }))}
                  placeholder="e.g., Acme Corporation"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Party Type</Label>
                <Select value={form.party_type} onValueChange={v => setForm(p => ({ ...p, party_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="corporation">Corporation</SelectItem>
                    <SelectItem value="government">Government Entity</SelectItem>
                    <SelectItem value="organization">Organization</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Jurisdiction (Optional)</Label>
                <Select value={form.jurisdiction || "all"} onValueChange={v => setForm(p => ({ ...p, jurisdiction: v === "all" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="All jurisdictions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Jurisdictions</SelectItem>
                    {JURISDICTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" onClick={analyze} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />}
                Search History
              </Button>
            </CardContent>
          </Card>
          
          {result && (
            <div className="lg:col-span-2 space-y-4 animate-fade-in">
              <Card>
                <CardHeader>
                  <CardTitle>{result.party_name}</CardTitle>
                  <CardDescription className="capitalize">{result.party_type}</CardDescription>
                </CardHeader>
              </Card>
              
              <div className="grid gap-4 md:grid-cols-2">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-6">
                    <p className="text-sm text-blue-700 mb-1">Cases as Plaintiff</p>
                    <p className="text-4xl font-bold text-blue-700">{result.total_cases_as_plaintiff}</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-6">
                    <p className="text-sm text-amber-700 mb-1">Cases as Defendant</p>
                    <p className="text-4xl font-bold text-amber-700">{result.total_cases_as_defendant}</p>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader><CardTitle>Case Type Distribution</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(result.case_type_distribution).map(([type, count]) => (
                      <div key={type} className="flex items-center gap-3">
                        <span className="text-sm capitalize w-32">{type.replace(/_/g, ' ')}</span>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full" 
                            style={{ width: `${(count / (result.total_cases_as_plaintiff + result.total_cases_as_defendant)) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium w-8 text-right">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader><CardTitle>Resolution Patterns</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(result.settlement_vs_trial_ratio).map(([type, ratio]) => (
                        <div key={type} className="flex justify-between">
                          <span className="text-sm capitalize">{type}</span>
                          <span className="font-semibold">{(ratio * 100).toFixed(0)}%</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader><CardTitle>Litigation Metrics</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm">Avg Duration</span>
                        <span className="font-semibold">{Math.round(result.average_case_duration_days / 30)} months</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Total Cases</span>
                        <span className="font-semibold">{result.total_cases_as_plaintiff + result.total_cases_as_defendant}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader><CardTitle>Data Sources</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {result.data_sources.map((src, i) => (
                      <Badge key={i} variant="outline">{src}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default PartyHistoryPage;
